import { confirm } from 'devextreme/ui/dialog';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import { deleteApi } from '../../apiUtils';
import { useTranslation } from '../../contexts/translation';
// abych odlišil od nativního File
import { File as FileType } from '../../types/file.types';
import { Hyperlink } from '../../types/hyperlink.types';
import { API_URL, fetcher } from '../../utils/apiUrl';
import { DataGridButton, IColumnType, IDataType, SisDataGrid, SisDataGridColumns } from '../table';
import { AttachmentEditItem, AttachmentEditModal, AttachmentKind } from './AttachmentEditModal';
import './FileManager.scss';

interface TabFilesProps {
  id: number;
  entityName: string;
  isReadonly: boolean;
  onFileCountChange?: any;
  hideFileList?: boolean;
  /**
   * Nastaveno = do gridu se přidají i přílohy navázané entity (u stroje přílohy jeho požadavků,
   * u požadavku přílohy jeho stroje). Nevyplněno = zobrazují se jen vlastní přílohy entity,
   * což je případ nákladů na údržbu (#737).
   */
  mainEntity?: 'asset' | 'task';
  assetId?: number;
  assetName?: string;
  /**
   * Pokud je vyplněno (task navázaný na AssetCycle), přidají se do gridu i (read-only)
   * přílohy a hyperlinky daného AssetCycle - stejně jako se zobrazují přílohy assetu tasku.
   */
  assetCycleId?: number | null;
}

export function TabFilesDatagrid(props: TabFilesProps) {
  const { translate } = useTranslation();
  const transFormName = 'File';
  const hasRelativeEntity = !!props.mainEntity;
  const hasAssetCycle = !!props.assetCycleId;

  const apiUrlListFilesMain = `${API_URL.FILE_LIST}?entity=${props.entityName}&entityId=${props.id}`;
  const apiUrlListHyperlinksMain = `${API_URL.HYPERLINK_LIST_FILTERED}?entity=${props.entityName}&entityId=${props.id}`;
  const apiUrlListFilesRelativeEntity =
    props.mainEntity === 'task'
      ? `${API_URL.FILE_LIST}?entity=Asset&entityId=${props.assetId}`
      : `${API_URL.FILE_LIST_BY_ASSET}?assetId=${props.id}`;
  const apiUrlListHyperlinksRelativeEntity =
    props.mainEntity === 'task'
      ? `${API_URL.HYPERLINK_LIST_FILTERED}?entity=Asset&entityId=${props.assetId}`
      : `${API_URL.HYPERLINK_LIST_BY_ASSET}?assetId=${props.id}`;
  const apiUrlListFilesAssetCycle = `${API_URL.FILE_LIST}?entity=AssetCycle&entityId=${props.assetCycleId}`;
  const apiUrlListHyperlinksAssetCycle = `${API_URL.HYPERLINK_LIST_FILTERED}?entity=AssetCycle&entityId=${props.assetCycleId}`;

  // Načteme subory
  const { data: filesMain, mutate: mutateFilesMain } = useSWR<FileType[]>(
    props.entityName && props.id ? `${apiUrlListFilesMain}` : null,
    fetcher,
  );
  const { data: filesRelativeEntity, mutate: mutateFilesRelativeEntity } = useSWR<FileType[]>(
    hasRelativeEntity && props.entityName && props.id ? `${apiUrlListFilesRelativeEntity}` : null,
    fetcher,
  );
  // Načteme hyperlinks
  const { data: hyperlinksMain, mutate: mutateHyperlinksMain } = useSWR<Hyperlink[]>(
    props.entityName && props.id ? `${apiUrlListHyperlinksMain}` : null,
    fetcher,
  );
  const { data: hyperlinksRelativeEntity, mutate: mutateHyperlinksRelativeEntity } = useSWR<
    Hyperlink[]
  >(
    hasRelativeEntity && props.entityName && props.id
      ? `${apiUrlListHyperlinksRelativeEntity}`
      : null,
    fetcher,
  );
  // Načteme soubory a hyperlinky navázaného AssetCycle (read-only)
  const { data: filesAssetCycle, mutate: mutateFilesAssetCycle } = useSWR<FileType[]>(
    hasAssetCycle ? `${apiUrlListFilesAssetCycle}` : null,
    fetcher,
  );
  const { data: hyperlinksAssetCycle, mutate: mutateHyperlinksAssetCycle } = useSWR<Hyperlink[]>(
    hasAssetCycle ? `${apiUrlListHyperlinksAssetCycle}` : null,
    fetcher,
  );

  const reloadAll = () => {
    mutateFilesMain();
    mutateHyperlinksMain();
    if (hasRelativeEntity) {
      mutateFilesRelativeEntity();
      mutateHyperlinksRelativeEntity();
    }
    if (hasAssetCycle) {
      mutateFilesAssetCycle();
      mutateHyperlinksAssetCycle();
    }
    if (props.onFileCountChange) props.onFileCountChange();
  };

  const [modalVisible, setModalVisible] = useState(false);
  const [editItem, setEditItem] = useState<AttachmentEditItem | null>(null);

  const handleNew = () => {
    setEditItem(null);
    setModalVisible(true);
  };

  const handleEdit = (rowData: any) => {
    setEditItem({
      id: rowData.id,
      name: rowData.name ?? '',
      description: rowData.description ?? '',
      url: rowData.url ?? '',
      categoryId: rowData.categoryId ?? null,
    });
    setModalVisible(true);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setEditItem(null);
  };

  const handleFileDelete = async (id: string) => {
    const idArray = id.split('_');
    const itemId = Number(idArray[1]);
    const entityType = idArray[0];
    if (
      !(await confirm(
        translate!(`Are you sure you want to delete ${entityType}?`, transFormName),
        translate!('Confirm', ''),
      ))
    )
      return;

    if (entityType === AttachmentKind.File) {
      await deleteApi({
        url: `${API_URL.FILE_DELETE_ITEM}?entity=${props.entityName}&entityId=${props.id}&id=${itemId}`,
        id: itemId,
        callAfterSuccess: reloadAll,
      });
    }

    if (entityType === AttachmentKind.Hyperlink) {
      await deleteApi({
        url: `${API_URL.HYPERLINK_DELETE}?id=${itemId}`,
        id: itemId,
        callAfterSuccess: reloadAll,
      });
    }
  };

  const handlePreview = async (rowData: any) => {
    const idArray = rowData.id.split('_');
    const entityId = Number(idArray[1]);
    const entityType = idArray[0];

    if (entityType === AttachmentKind.Hyperlink) {
      const raw: string = rowData.url;
      const href = raw.startsWith('http://') || raw.startsWith('https://') ? raw : `https://${raw}`;
      window.open(href, '_blank');
      return;
    }

    const response = await fetch(
      `${API_URL.FILE_ITEM}?entity=${rowData.fileEntityName}&id=${entityId}`,
      { method: 'GET', headers: { 'Content-Type': 'application/json' } },
    );
    const blob = await response.blob();
    const fileUrl = URL.createObjectURL(blob);

    const previewable = /^(image\/|application\/pdf|text\/|video\/|audio\/)/.test(
      rowData.contentType,
    );
    if (previewable) {
      window.open(fileUrl, '_blank');
    } else {
      const anchor = document.createElement('a');
      anchor.href = fileUrl;
      anchor.download = rowData.name;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    }
  };

  const handleDownload = async (rowData: any) => {
    const idArray = rowData.id.split('_');
    const entityId = Number(idArray[1]);

    const response = await fetch(
      `${API_URL.FILE_ITEM}?entity=${rowData.fileEntityName}&id=${entityId}`,
      { method: 'GET', headers: { 'Content-Type': 'application/json' } },
    );
    const blob = await response.blob();
    const fileUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = fileUrl;
    anchor.download = rowData.name;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  const onToolbarPreparing = (e: any) => {
    e.toolbarOptions.items.unshift({
      location: 'after',
      widget: 'dxButton',
      options: {
        icon: 'add',
        type: 'default',
        stylingMode: 'contained',
        hint: translate!('Add attachment', transFormName),
        onClick: handleNew,
        visible: !props.isReadonly,
      },
    });
  };

  const Columns = (): SisDataGridColumns => {
    const columns: any = [];

    columns.push({
      type: IColumnType.buttons,
      cellRender: (column: any) => {
        return (
          <>
            <DataGridButton
              hint={''}
              icon="find"
              cellData={column.data}
              onClick={() => handlePreview(column.data)}
            />
            {column.data.id.startsWith(`${AttachmentKind.File}_`) && (
              <DataGridButton
                hint={''}
                icon="download"
                cellData={column.data}
                onClick={() => handleDownload(column.data)}
              />
            )}
            {column.data.enableChange && !props.isReadonly && (
              <>
                <DataGridButton
                  hint={''}
                  icon="edit"
                  cellData={column.data}
                  onClick={() => handleEdit(column.data)}
                />
                <DataGridButton
                  hint={''}
                  icon="trash"
                  cellData={column.data}
                  onClick={() => handleFileDelete(column.data.id)}
                />
              </>
            )}
          </>
        );
      },
      width: '8%',
    });

    if (props.mainEntity === 'task')
      columns.push({
        dataField: 'assetName',
        caption: 'Asset name',
        width: '15%',
        cellRender: (column: any) => {
          if (!column.data.assetName) return <></>;
          return <b>{column.data.assetName}</b>;
        },
      });

    if (props.mainEntity === 'asset')
      columns.push({
        dataField: 'taskCustomId',
        caption: 'Task id',
        width: '7%',
        cellRender: (column: any) => {
          if (!column.data.taskCustomId) return <></>;
          return (
            <Link
              to={`/tasks/edit/${column.data.taskId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <b>{column.data.taskCustomId}</b>
            </Link>
          );
        },
      });

    columns.push(
      {
        dataField: 'name',
        caption: 'Name',
        width: props.mainEntity === 'task' ? '15%' : '20%',
      },
      {
        dataField: 'categoryName',
        caption: 'Category',
        width: '12%',
      },
      {
        dataField: 'description',
        caption: 'Description',
        width: props.mainEntity === 'task' ? '18%' : '20%',
      },
      {
        dataField: 'url',
        caption: 'URL',
        width: '15%',
        cellRender: (column: any) => {
          if (!column.data.url) return <></>;
          const raw: string = column.data.url;
          const href =
            raw.startsWith('http://') || raw.startsWith('https://') ? raw : `https://${raw}`;
          return (
            <a href={href} target="_blank" rel="noopener noreferrer">
              {raw}
            </a>
          );
        },
      },
      {
        dataType: IDataType.dateTime,
        dataField: 'created',
        caption: 'Created',
        width: '11%',
      },
      {
        dataField: 'createdBy',
        caption: 'Created by',
        width: '10%',
      },
      {
        dataType: IDataType.number,
        dataField: 'sizeInBytes',
        caption: 'Size',
        width: '5%',
        calculateCellValue: (row: any) =>
          row.sizeInBytes > 0 ? Math.round((row.sizeInBytes / 1024) * 10) / 10 : null,
      },
    );

    return columns;
  };

  const filesMerged = useMemo(() => {
    if (!filesMain || !hyperlinksMain) return [];
    if (hasRelativeEntity && (!filesRelativeEntity || !hyperlinksRelativeEntity)) return [];
    if (hasAssetCycle && (!filesAssetCycle || !hyperlinksAssetCycle)) return [];

    return [
      ...filesMain.map((file) => ({
        id: `${AttachmentKind.File}_${file.id}`,
        assetName: '',
        taskCustomId: '',
        taskId: undefined,
        name: file.name,
        categoryId: file.categoryId ?? null,
        categoryName: file.categoryName ?? '',
        description: file.description,
        url: '',
        created: file.created,
        createdBy: file.createdByName,
        sizeInBytes: file.sizeInBytes,
        enableChange: true,
        contentType: file.contentType,
        fileEntityName: props.entityName,
      })),
      ...hyperlinksMain.map((link) => ({
        id: `${AttachmentKind.Hyperlink}_${link.id}`,
        assetName: '',
        taskCustomId: '',
        taskId: undefined,
        name: link.name,
        categoryId: null,
        categoryName: '',
        description: '',
        url: link.uri,
        created: link.created,
        createdBy: link.createdByName,
        sizeInBytes: 0,
        enableChange: true,
        contentType: '',
        fileEntityName: '',
      })),
      ...(filesRelativeEntity ?? []).map((file) => ({
        id: `${AttachmentKind.File}_${file.id}`,
        assetName: props.assetName ? props.assetName : '',
        taskCustomId: file.taskCustomId ? file.taskCustomId : '',
        taskId: file.taskId ? file.taskId : undefined,
        name: file.name,
        categoryId: file.categoryId ?? null,
        categoryName: file.categoryName ?? '',
        description: file.description,
        url: '',
        created: file.created,
        createdBy: file.createdByName,
        sizeInBytes: file.sizeInBytes,
        enableChange: false,
        contentType: file.contentType,
        fileEntityName: props.mainEntity === 'task' ? 'Asset' : 'Task',
      })),
      ...(hyperlinksRelativeEntity ?? []).map((link) => ({
        id: `${AttachmentKind.Hyperlink}_${link.id}`,
        assetName: props.assetName ? props.assetName : '',
        taskCustomId: link.taskCustomId ? link.taskCustomId : '',
        taskId: link.taskId ? link.taskId : undefined,
        name: link.name,
        categoryId: null,
        categoryName: '',
        description: '',
        url: link.uri,
        created: link.created,
        createdBy: link.createdByName,
        sizeInBytes: 0,
        enableChange: false,
        contentType: '',
        fileEntityName: '',
      })),
      ...(filesAssetCycle ?? []).map((file) => ({
        id: `${AttachmentKind.File}_${file.id}`,
        assetName: '',
        taskCustomId: '',
        taskId: undefined,
        name: file.name,
        categoryId: file.categoryId ?? null,
        categoryName: file.categoryName ?? '',
        description: file.description,
        url: '',
        created: file.created,
        createdBy: file.createdByName,
        sizeInBytes: file.sizeInBytes,
        enableChange: false,
        contentType: file.contentType,
        fileEntityName: 'AssetCycle',
      })),
      ...(hyperlinksAssetCycle ?? []).map((link) => ({
        id: `${AttachmentKind.Hyperlink}_${link.id}`,
        assetName: '',
        taskCustomId: '',
        taskId: undefined,
        name: link.name,
        categoryId: null,
        categoryName: '',
        description: '',
        url: link.uri,
        created: link.created,
        createdBy: link.createdByName,
        sizeInBytes: 0,
        enableChange: false,
        contentType: '',
        fileEntityName: '',
      })),
    ];
  }, [
    filesMain,
    filesRelativeEntity,
    filesAssetCycle,
    hyperlinksMain,
    hyperlinksRelativeEntity,
    hyperlinksAssetCycle,
  ]);

  return (
    <div className={'file-manager responsive-paddings'}>
      {!props.hideFileList && (
        <div>
          <SisDataGrid
            data={filesMerged}
            keyExpr={'id'}
            transFormName={transFormName}
            columns={Columns()}
            columnChooser={false}
            allowColumnResizing={false}
            allowColumnReordering={false}
            onToolbarPreparing={onToolbarPreparing}
            enableGrouping
            pageSizeChangeVisible
            width={'100%'}
          />
        </div>
      )}
      {modalVisible && (
        <AttachmentEditModal
          entityName={props.entityName}
          entityId={props.id}
          isVisible={modalVisible}
          onClose={handleModalClose}
          onSuccess={reloadAll}
          editItem={editItem}
        />
      )}
    </div>
  );
}
